import { useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import {
  ShieldExclamationIcon,
  MagnifyingGlassIcon,
  CloudIcon,
  DocumentTextIcon,
} from '@heroicons/react/24/outline';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  Legend,
} from 'recharts';
import { format } from 'date-fns';
import { farmApi, diseaseApi, weatherApi, DiseaseInfo, DiseaseForecast } from '../lib/api';
import toast from 'react-hot-toast';

function RiskBadge({ level, color }: { level: string; color: string }) {
  return (
    <span
      className="px-2 py-1 rounded-full text-xs font-medium text-white"
      style={{ backgroundColor: color }}
    >
      {level.replace('_', ' ').toUpperCase()}
    </span>
  );
}

function DiseaseCard({ disease, onSelect }: { disease: DiseaseInfo; onSelect: () => void }) {
  return (
    <div
      onClick={onSelect}
      className="bg-white rounded-lg border p-4 hover:shadow-md transition-shadow cursor-pointer"
    >
      <div className="flex items-start justify-between">
        <div>
          <h4 className="font-medium text-gray-900">{disease.name}</h4>
          <p className="text-sm text-gray-500 italic">{disease.scientificName}</p>
        </div>
        <ShieldExclamationIcon className="h-5 w-5 text-orange-500" />
      </div>
      <div className="mt-3">
        <p className="text-xs text-gray-600">
          <strong>Crops:</strong> {disease.affectedCrops.join(', ')}
        </p>
        <p className="text-xs text-gray-600 mt-1 line-clamp-2">
          <strong>Symptoms:</strong> {disease.symptoms.slice(0, 2).join(', ')}
        </p>
      </div>
    </div>
  );
}

function DiseaseDetailModal({
  disease,
  onClose,
}: {
  disease: DiseaseInfo;
  onClose: () => void;
}) {
  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4">
        <div className="fixed inset-0 bg-black/50" onClick={onClose} />
        <div className="relative bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
          <div className="p-6">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h2 className="text-xl font-bold text-gray-900">{disease.name}</h2>
                <p className="text-gray-500 italic">{disease.scientificName}</p>
              </div>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600"
              >
                &times;
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">Affected Crops</h3>
                <div className="flex flex-wrap gap-2">
                  {disease.affectedCrops.map(crop => (
                    <span key={crop} className="px-2 py-1 bg-green-100 text-green-800 rounded text-sm">
                      {crop}
                    </span>
                  ))}
                </div>
              </div>

              <div>
                <h3 className="font-semibold text-gray-900 mb-2">Symptoms</h3>
                <ul className="list-disc list-inside text-sm text-gray-700 space-y-1">
                  {disease.symptoms.map((s, i) => <li key={i}>{s}</li>)}
                </ul>
              </div>

              <div>
                <h3 className="font-semibold text-gray-900 mb-2">Causes</h3>
                <ul className="list-disc list-inside text-sm text-gray-700 space-y-1">
                  {disease.causes.map((c, i) => <li key={i}>{c}</li>)}
                </ul>
              </div>

              <div>
                <h3 className="font-semibold text-gray-900 mb-2">Optimal Conditions for Disease</h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div className="bg-red-50 p-3 rounded-lg">
                    <p className="text-red-800">
                      <strong>Temperature:</strong> {disease.optimalConditions.temperature.min}-{disease.optimalConditions.temperature.max}°C
                    </p>
                  </div>
                  <div className="bg-blue-50 p-3 rounded-lg">
                    <p className="text-blue-800">
                      <strong>Humidity:</strong> {disease.optimalConditions.humidity.min}-{disease.optimalConditions.humidity.max}%
                    </p>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="font-semibold text-gray-900 mb-2">Treatments</h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  {disease.treatments.chemical.length > 0 && (
                    <div className="bg-purple-50 p-3 rounded-lg">
                      <p className="font-medium text-purple-900 mb-1">Chemical</p>
                      <ul className="text-purple-700 text-xs space-y-1">
                        {disease.treatments.chemical.map((t, i) => <li key={i}>• {t}</li>)}
                      </ul>
                    </div>
                  )}
                  {disease.treatments.biological.length > 0 && (
                    <div className="bg-green-50 p-3 rounded-lg">
                      <p className="font-medium text-green-900 mb-1">Biological</p>
                      <ul className="text-green-700 text-xs space-y-1">
                        {disease.treatments.biological.map((t, i) => <li key={i}>• {t}</li>)}
                      </ul>
                    </div>
                  )}
                  {disease.treatments.cultural.length > 0 && (
                    <div className="bg-orange-50 p-3 rounded-lg">
                      <p className="font-medium text-orange-900 mb-1">Cultural</p>
                      <ul className="text-orange-700 text-xs space-y-1">
                        {disease.treatments.cultural.map((t, i) => <li key={i}>• {t}</li>)}
                      </ul>
                    </div>
                  )}
                  {disease.treatments.organic.length > 0 && (
                    <div className="bg-teal-50 p-3 rounded-lg">
                      <p className="font-medium text-teal-900 mb-1">Organic</p>
                      <ul className="text-teal-700 text-xs space-y-1">
                        {disease.treatments.organic.map((t, i) => <li key={i}>• {t}</li>)}
                      </ul>
                    </div>
                  )}
                </div>
              </div>

              <div>
                <h3 className="font-semibold text-gray-900 mb-2">Prevention Measures</h3>
                <ul className="list-disc list-inside text-sm text-gray-700 space-y-1">
                  {disease.preventionMeasures.map((p, i) => <li key={i}>{p}</li>)}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Diseases() {
  const [selectedFarm, setSelectedFarm] = useState<string>('');
  const [selectedDisease, setSelectedDisease] = useState<DiseaseInfo | null>(null);
  const [searchSymptoms, setSearchSymptoms] = useState<string>('');
  const [forecast, setForecast] = useState<DiseaseForecast | null>(null);
  const [activeTab, setActiveTab] = useState<'library' | 'forecast' | 'search'>('library');

  const { data: farms = [] } = useQuery({
    queryKey: ['farms'],
    queryFn: farmApi.list,
  });

  const { data: allDiseases, isLoading: diseasesLoading } = useQuery({
    queryKey: ['diseases-all'],
    queryFn: diseaseApi.getAll,
  });

  const searchMutation = useMutation({
    mutationFn: (symptoms: string[]) => diseaseApi.searchBySymptoms(symptoms),
    onSuccess: (data) => {
      if (data.data.count === 0) {
        toast.error('No diseases found matching symptoms');
      } else {
        toast.success(`Found ${data.data.count} matching diseases`);
      }
    },
  });

  const forecastMutation = useMutation({
    mutationFn: diseaseApi.forecast,
    onSuccess: (response) => {
      setForecast(response.data);
      toast.success('Disease forecast generated');
    },
    onError: () => {
      toast.error('Failed to generate forecast');
    },
  });

  const handleForecast = async () => {
    const farm = farms.find(f => f.id === selectedFarm);
    if (!farm) {
      toast.error('Please select a farm');
      return;
    }

    try {
      // Fetch real weather forecast from API
      const weatherResult = await weatherApi.getForecast(selectedFarm, 7);
      const weatherData = weatherResult.data || weatherResult.forecast || [];

      if (!weatherData || weatherData.length === 0) {
        toast.error('No weather data available for this farm');
        return;
      }

      // Transform weather data to the format expected by disease forecast
      const weatherForecast = weatherData.map((day: { date: string; temperatureMin: number; temperatureMax: number; humidity: number; precipitation: number }) => ({
        date: day.date,
        temperatureMin: day.temperatureMin,
        temperatureMax: day.temperatureMax,
        temperatureMean: (day.temperatureMin + day.temperatureMax) / 2,
        humidity: day.humidity,
        precipitation: day.precipitation || 0,
      }));

      forecastMutation.mutate({
        farmId: selectedFarm,
        cropType: farm.cropType || 'corn',
        weatherForecast,
      });
    } catch {
      toast.error('Failed to fetch weather data');
    }
  };

  const handleSearch = () => {
    const symptoms = searchSymptoms.split(',').map(s => s.trim()).filter(Boolean);
    if (symptoms.length === 0) {
      toast.error('Please enter symptoms to search');
      return;
    }
    searchMutation.mutate(symptoms);
  };

  const forecastChartData = forecast?.dailyRisks.map(day => ({
    date: format(new Date(day.date), 'MMM d'),
    ...day.diseases.reduce((acc, d) => ({
      ...acc,
      [d.diseaseName]: d.riskScore,
    }), {}),
  })) || [];

  const diseases = searchMutation.data?.data?.diseases || allDiseases?.data?.diseases || [];

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Disease Management</h1>
        <p className="text-gray-500">Disease detection, forecasting, and management</p>
      </div>

      {/* Tabs */}
      <div className="border-b">
        <nav className="flex space-x-8">
          {[
            { id: 'library', label: 'Disease Library', icon: DocumentTextIcon },
            { id: 'forecast', label: 'Risk Forecast', icon: CloudIcon },
            { id: 'search', label: 'Symptom Search', icon: MagnifyingGlassIcon },
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as typeof activeTab)}
              className={`flex items-center py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === tab.id
                  ? 'border-green-500 text-green-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <tab.icon className="h-5 w-5 mr-2" />
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Disease Library Tab */}
      {activeTab === 'library' && (
        <div className="space-y-6">
          {diseasesLoading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600" />
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {diseases.map(disease => (
                <DiseaseCard
                  key={disease.id}
                  disease={disease}
                  onSelect={() => setSelectedDisease(disease)}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {/* Forecast Tab */}
      {activeTab === 'forecast' && (
        <div className="space-y-6">
          {/* Farm Selection */}
          <div className="bg-white rounded-xl shadow-sm border p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Generate Disease Forecast</h2>
            <div className="flex items-end space-x-4">
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select Farm
                </label>
                <select
                  value={selectedFarm}
                  onChange={(e) => setSelectedFarm(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500"
                >
                  <option value="">Choose a farm...</option>
                  {farms.map(farm => (
                    <option key={farm.id} value={farm.id}>
                      {farm.name} - {farm.cropType || 'Unknown crop'}
                    </option>
                  ))}
                </select>
              </div>
              <button
                onClick={handleForecast}
                disabled={!selectedFarm || forecastMutation.isPending}
                className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center"
              >
                {forecastMutation.isPending ? (
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" />
                ) : (
                  <>
                    <CloudIcon className="h-5 w-5 mr-2" />
                    Generate Forecast
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Forecast Results */}
          {forecast && (
            <div className="space-y-6">
              {/* Overall Risk */}
              <div className="bg-white rounded-xl shadow-sm border p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">Overall Risk Assessment</h3>
                  <RiskBadge
                    level={forecast.overallRisk}
                    color={forecast.overallRisk === 'low' ? '#22c55e' : forecast.overallRisk === 'moderate' ? '#f59e0b' : '#ef4444'}
                  />
                </div>
                <p className="text-gray-600">
                  Forecast period: {format(new Date(forecast.forecastPeriod.start), 'MMM d')} - {format(new Date(forecast.forecastPeriod.end), 'MMM d, yyyy')}
                </p>
              </div>

              {/* Risk Chart */}
              <div className="bg-white rounded-xl shadow-sm border p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">7-Day Risk Trend</h3>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={forecastChartData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                      <YAxis domain={[0, 100]} tick={{ fontSize: 12 }} />
                      <Tooltip />
                      <Legend />
                      {forecast.dailyRisks[0]?.diseases.map((d) => (
                        <Area
                          key={d.diseaseId}
                          type="monotone"
                          dataKey={d.diseaseName}
                          stackId="1"
                          stroke={d.riskColor}
                          fill={d.riskColor}
                          fillOpacity={0.6}
                        />
                      ))}
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Daily Risk Details */}
              <div className="bg-white rounded-xl shadow-sm border p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Daily Risk Details</h3>
                <div className="space-y-4">
                  {forecast.dailyRisks.slice(0, 3).map(day => (
                    <div key={day.date} className="border-b pb-4 last:border-0">
                      <p className="font-medium text-gray-900 mb-2">
                        {format(new Date(day.date), 'EEEE, MMM d')}
                      </p>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                        {day.diseases.map(risk => (
                          <div
                            key={risk.diseaseId}
                            className="bg-gray-50 rounded-lg p-3"
                          >
                            <div className="flex items-center justify-between mb-1">
                              <span className="font-medium text-sm">{risk.diseaseName}</span>
                              <RiskBadge level={risk.riskLevel} color={risk.riskColor} />
                            </div>
                            <p className="text-xs text-gray-600">
                              Score: {risk.riskScore.toFixed(0)}%
                            </p>
                            {risk.recommendations.length > 0 && (
                              <p className="text-xs text-green-700 mt-1">
                                {risk.recommendations[0]}
                              </p>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {!forecast && (
            <div className="bg-white rounded-xl shadow-sm border p-12 text-center">
              <CloudIcon className="h-16 w-16 mx-auto text-gray-300 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No Forecast Generated</h3>
              <p className="text-gray-500">
                Select a farm above to generate a 7-day disease risk forecast
              </p>
            </div>
          )}
        </div>
      )}

      {/* Search Tab */}
      {activeTab === 'search' && (
        <div className="space-y-6">
          <div className="bg-white rounded-xl shadow-sm border p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Search by Symptoms</h2>
            <div className="flex items-end space-x-4">
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Enter Symptoms (comma-separated)
                </label>
                <input
                  type="text"
                  value={searchSymptoms}
                  onChange={(e) => setSearchSymptoms(e.target.value)}
                  placeholder="e.g., yellowing leaves, white powder, wilting"
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500"
                />
              </div>
              <button
                onClick={handleSearch}
                disabled={searchMutation.isPending}
                className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center"
              >
                {searchMutation.isPending ? (
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" />
                ) : (
                  <>
                    <MagnifyingGlassIcon className="h-5 w-5 mr-2" />
                    Search
                  </>
                )}
              </button>
            </div>
          </div>

          {searchMutation.data && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {searchMutation.data.data.diseases.map(disease => (
                <DiseaseCard
                  key={disease.id}
                  disease={disease}
                  onSelect={() => setSelectedDisease(disease)}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {/* Disease Detail Modal */}
      {selectedDisease && (
        <DiseaseDetailModal
          disease={selectedDisease}
          onClose={() => setSelectedDisease(null)}
        />
      )}
    </div>
  );
}
